import { Circle, HStack, useBreakpointValue, VStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';

// Define loading messages in sequence
const defaultLoadingMessages = [
  'Sending your message...',
  'Creating your chat...',
  'Generating responses...',
];

const projectCreationMessages = [
  'Collecting relevant Personas',
  'Creating additional subtopics',
  'Generating relevant questions',
];

// Animation keyframes
const bounce = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  `;

// Types
interface SimpleDotsLoaderProps {
  isLoading: boolean;
  loadingText?: string;
  size?: 'sm' | 'md' | 'lg';
  isProjectCreation?: boolean;
}

const CreateChatLoading: React.FC<SimpleDotsLoaderProps> = ({
  isLoading,
  loadingText,
  size = 'md',
  isProjectCreation = false,
}) => {
  const [message, setMessage] = useState<string>('');
  const [messageIndex, setMessageIndex] = useState<number>(0);

  // Dynamic styles based on size
  const dotSize = useBreakpointValue({
    sm: '8px',
    md: '12px',
    lg: '16px',
  });
  // Sequential message display
  useEffect(() => {
    if (!isLoading) return;

    // If custom loading text is provided, just use that
    if (loadingText) {
      setMessage(loadingText);
      return;
    }

    const messages = isProjectCreation
      ? projectCreationMessages
      : defaultLoadingMessages;

    // Set initial message
    setMessage(messages[0]);
    setMessageIndex(0);

    // Set up interval to change messages sequentially
    const interval = setInterval(() => {
      setMessageIndex((prevIndex) => {
        // If we've reached the last message, clear the interval
        if (prevIndex >= messages.length - 1) {
          clearInterval(interval);
          return messages.length - 1; // Stay on last message
        }
        // Otherwise, move to next message
        return prevIndex + 1;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [isLoading, loadingText, isProjectCreation]);

  // Update message when messageIndex changes
  useEffect(() => {
    if (loadingText) return; // Skip if custom text is provided
    const messages = isProjectCreation
      ? projectCreationMessages
      : defaultLoadingMessages;
    if (messageIndex < messages.length) {
      setMessage(messages[messageIndex]);
    }
  }, [messageIndex, loadingText, isProjectCreation]);

  if (!isLoading) return null;

  return (
    <VStack spacing={4} w="fit-content">
      <HStack spacing={4} justifyContent="center">
        <Circle
          size={dotSize}
          bg={HelivanColor.hover.lightBlue}
          animation={`${bounce} ${0.8 * 1.2}s infinite ease-in-out`}
        />
        <Circle
          size={dotSize}
          bg={HelivanColor.hover.lightBlue}
          animation={`${bounce} ${0.8 * 2.2}s infinite ease-in-out`}
        />
        <Circle
          size={dotSize}
          bg={HelivanColor.hover.lightBlue}
          animation={`${bounce} ${0.8 * 3.2}s infinite ease-in-out`}
        />
      </HStack>

      <HelivanText variant="largeBody" colorOverride={HelivanColor.text.gray}>
        {message}
      </HelivanText>
    </VStack>
  );
};

export default CreateChatLoading;
